// Frontend startup diagnostic — fires once on app mount. Logs the SPA's
// own deploy stamp + the API's /health response so the browser console
// can answer "which version am I looking at and is the API reachable?"
// in two seconds without any network tab spelunking.
//
// Both fetches are non-blocking; failures are logged but do not throw.

const API_URL = import.meta.env.VITE_API_URL || '';

type FrontendVersion = {
  version: string;
  commit: string;
  built_at: string;
};

type ApiHealth = {
  status: string;
  mode?: string;
  version?: string;
  commit?: string;
  built_at?: string;
  db?: string;
};

async function fetchFrontendVersion(): Promise<FrontendVersion | { error: string }> {
  // In Vite dev there's no built /version.json — the dev server falls back
  // to index.html for unknown paths, so JSON.parse would choke on `<!doctype`.
  if (import.meta.env.DEV) {
    return { version: 'dev', commit: 'dev', built_at: 'dev' };
  }
  try {
    const r = await fetch('/version.json', { cache: 'no-store' });
    if (!r.ok) return { error: `HTTP ${r.status}` };
    return (await r.json()) as FrontendVersion;
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

async function fetchApiHealth(): Promise<ApiHealth | { error: string }> {
  if (!API_URL) return { error: 'VITE_API_URL is not set' };
  try {
    const r = await fetch(`${API_URL}/health`, {
      method: 'GET',
      // No credentials — /health is public and we want to confirm
      // basic connectivity, not test the cookie path.
      credentials: 'omit',
    });
    const body = await r.json();
    if (!r.ok) return { error: `HTTP ${r.status}`, ...body };
    return body as ApiHealth;
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

export async function runStartupDiagnostic(): Promise<void> {
  // Fire both in parallel. Resolve regardless of individual errors.
  const [fe, api] = await Promise.all([fetchFrontendVersion(), fetchApiHealth()]);

  // Group output so it's collapsible in DevTools and easy to ignore.
  const label = '%c[geniac] startup diagnostic';
  const labelStyle = 'color:#f59e0b;font-weight:700';

  // eslint-disable-next-line no-console
  console.groupCollapsed(label, labelStyle);
  // eslint-disable-next-line no-console
  console.log('frontend:', fe);
  // eslint-disable-next-line no-console
  console.log('API URL:', API_URL || '(not configured)');
  // eslint-disable-next-line no-console
  console.log('API /health:', api);

  if ('error' in api) {
    // eslint-disable-next-line no-console
    console.error('[geniac] API health check failed — check VITE_API_URL, CORS, and the staging/prod API:', api);
  } else if (api.db && api.db !== 'ok') {
    // eslint-disable-next-line no-console
    console.error('[geniac] API up but DB unhealthy:', api);
  }
  // eslint-disable-next-line no-console
  console.groupEnd();
}
